// src/components/CookiesPolicy.jsx
import { Cog6ToothIcon, ChartBarIcon, ShieldCheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';

function CookiesPolicy() {
  const lastUpdated = "June 24, 2025";
  const [openSections, setOpenSections] = useState({});

  const toggleSection = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-6 py-12 pt-16">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center text-white text-2xl">
                🍪
              </div>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Cookies Policy</h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Learn how we use cookies to improve your experience and protect your privacy on Connect & Co.
            </p>
            <p className="text-sm text-gray-500 mt-4">Last updated: {lastUpdated}</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-xl shadow-sm p-8 space-y-8">

          {/* Cookie Control Panel */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="font-semibold text-blue-800 mb-3 flex items-center">
              <Cog6ToothIcon className="w-5 h-5 mr-2" />
              Your Cookie Preferences
            </h3>
            <p className="text-blue-700 text-sm mb-4">
              You have control over which cookies we use. Essential cookies cannot be disabled as they're necessary for the website to function.
            </p>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-white rounded p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-800">Essential</span>
                  <span className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded">Required</span>
                </div>
                <p className="text-xs text-gray-600">Security, authentication, basic functionality</p>
              </div>
              <div className="bg-white rounded p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-800">Analytics</span>
                  <button className="text-xs bg-green-200 text-green-700 px-2 py-1 rounded">Enabled</button>
                </div>
                <p className="text-xs text-gray-600">Help us improve our service</p>
              </div>
              <div className="bg-white rounded p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-800">Marketing</span>
                  <button className="text-xs bg-red-200 text-red-700 px-2 py-1 rounded">Disabled</button>
                </div>
                <p className="text-xs text-gray-600">Personalized recommendations</p>
              </div>
            </div>
          </div>

          {/* What Are Cookies */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">What Are Cookies?</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Cookies are small text files stored on your device when you visit websites. They help websites remember your preferences, 
              improve your experience, and provide insights into how the website is used.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="p-4 border border-gray-200 rounded-lg">
                <h3 className="font-semibold text-gray-800 mb-2 flex items-center">
                  <ShieldCheckIcon className="w-5 h-5 text-green-500 mr-2" />
                  Safe & Secure
                </h3>
                <p className="text-sm text-gray-600">
                  Cookies cannot access your personal files, install software, or harm your device. They only store small pieces of text.
                </p>
              </div>
              <div className="p-4 border border-gray-200 rounded-lg">
                <h3 className="font-semibold text-gray-800 mb-2 flex items-center">
                  <Cog6ToothIcon className="w-5 h-5 text-blue-500 mr-2" />
                  Controllable
                </h3>
                <p className="text-sm text-gray-600">
                  You can manage, disable, or delete cookies through your browser settings at any time.
                </p>
              </div>
            </div>
          </section>

          {/* Types of Cookies We Use */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Types of Cookies We Use</h2>
            
            <div className="space-y-4">
              {/* Essential Cookies */}
              <div className="border border-gray-200 rounded-lg">
                <button 
                  onClick={() => toggleSection('essential')}
                  className="w-full p-4 text-left flex items-center justify-between hover:bg-gray-50"
                >
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                    <div>
                      <h3 className="font-semibold text-gray-800">Essential Cookies</h3>
                      <p className="text-sm text-gray-600">Required for basic website functionality</p>
                    </div>
                  </div>
                  <span className="text-gray-400">{openSections.essential ? '−' : '+'}</span>
                </button>
                {openSections.essential && (
                  <div className="px-4 pb-4">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left p-2">Cookie Name</th>
                          <th className="text-left p-2">Purpose</th>
                          <th className="text-left p-2">Duration</th>
                        </tr>
                      </thead>
                      <tbody className="text-gray-600">
                        <tr className="border-t">
                          <td className="p-2 font-mono text-xs">session_id</td>
                          <td className="p-2">User authentication and login state</td>
                          <td className="p-2">Session</td>
                        </tr>
                        <tr className="border-t">
                          <td className="p-2 font-mono text-xs">csrf_token</td>
                          <td className="p-2">Security protection against attacks</td>
                          <td className="p-2">Session</td>
                        </tr>
                        <tr className="border-t">
                          <td className="p-2 font-mono text-xs">cookie_consent</td>
                          <td className="p-2">Remember your cookie preferences</td>
                          <td className="p-2">1 year</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Analytics Cookies */}
              <div className="border border-gray-200 rounded-lg">
                <button 
                  onClick={() => toggleSection('analytics')}
                  className="w-full p-4 text-left flex items-center justify-between hover:bg-gray-50"
                >
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
                    <div>
                      <h3 className="font-semibold text-gray-800">Analytics Cookies</h3>
                      <p className="text-sm text-gray-600">Help us understand how you use our website</p>
                    </div>
                  </div>
                  <span className="text-gray-400">{openSections.analytics ? '−' : '+'}</span>
                </button>
                {openSections.analytics && (
                  <div className="px-4 pb-4">
                    <div className="mb-4 p-3 bg-blue-50 rounded">
                      <p className="text-sm text-blue-700">
                        <strong>Privacy First:</strong> All analytics data is anonymized and aggregated. We never track personal information for analytics purposes.
                      </p>
                    </div>
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left p-2">Cookie Name</th>
                          <th className="text-left p-2">Purpose</th>
                          <th className="text-left p-2">Duration</th>
                        </tr>
                      </thead>
                      <tbody className="text-gray-600">
                        <tr className="border-t">
                          <td className="p-2 font-mono text-xs">_analytics_session</td>
                          <td className="p-2">Track page views and user interactions</td>
                          <td className="p-2">30 minutes</td>
                        </tr>
                        <tr className="border-t">
                          <td className="p-2 font-mono text-xs">_page_metrics</td>
                          <td className="p-2">Measure website performance and errors</td>
                          <td className="p-2">24 hours</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Functional Cookies */}
              <div className="border border-gray-200 rounded-lg">
                <button 
                  onClick={() => toggleSection('functional')}
                  className="w-full p-4 text-left flex items-center justify-between hover:bg-gray-50"
                >
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-purple-500 rounded-full mr-3"></div>
                    <div>
                      <h3 className="font-semibold text-gray-800">Functional Cookies</h3>
                      <p className="text-sm text-gray-600">Enhance your experience with personalized features</p>
                    </div>
                  </div>
                  <span className="text-gray-400">{openSections.functional ? '−' : '+'}</span>
                </button>
                {openSections.functional && (
                  <div className="px-4 pb-4">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left p-2">Cookie Name</th>
                          <th className="text-left p-2">Purpose</th>
                          <th className="text-left p-2">Duration</th>
                        </tr>
                      </thead>
                      <tbody className="text-gray-600">
                        <tr className="border-t">
                          <td className="p-2 font-mono text-xs">user_preferences</td>
                          <td className="p-2">Remember your timezone and location preferences</td>
                          <td className="p-2">6 months</td>
                        </tr>
                        <tr className="border-t">
                          <td className="p-2 font-mono text-xs">event_filters</td>
                          <td className="p-2">Save your event search and filter preferences</td>
                          <td className="p-2">30 days</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Third-Party Cookies */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Third-Party Cookies</h2>
            <p className="text-gray-700 mb-4">
              We use select third-party services that may place their own cookies. We carefully vet all partners to ensure they meet our privacy standards.
            </p>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 border border-gray-200 rounded-lg">
                <h3 className="font-semibold text-gray-800 mb-2">Google Analytics (Optional)</h3>
                <p className="text-sm text-gray-600 mb-2">
                  Helps us understand website usage patterns. Data is anonymized and aggregated.
                </p>
                <a href="https://policies.google.com/privacy" className="text-xs text-purple-600 hover:text-purple-700">
                  Google Privacy Policy →
                </a>
              </div>
              <div className="p-4 border border-gray-200 rounded-lg">
                <h3 className="font-semibold text-gray-800 mb-2">Email Service Provider</h3>
                <p className="text-sm text-gray-600 mb-2">
                  Powers our email communications and tracks email engagement for opt-in subscribers.
                </p>
                <p className="text-xs text-gray-500">Data processing agreement in place</p>
              </div>
            </div>
          </section>

          {/* Managing Cookies */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Managing Your Cookies</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Browser Settings</h3>
            <p className="text-gray-700 mb-4">
              You can control cookies through your browser settings. Here's how to manage cookies in popular browsers:
            </p>
            
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-800 mb-2">Chrome</h4>
                <p className="text-sm text-gray-600">Settings → Privacy & Security → Cookies and other site data</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-800 mb-2">Firefox</h4>
                <p className="text-sm text-gray-600">Options → Privacy & Security → Cookies and Site Data</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-800 mb-2">Safari</h4>
                <p className="text-sm text-gray-600">Preferences → Privacy → Manage Website Data</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-800 mb-2">Edge</h4>
                <p className="text-sm text-gray-600">Settings → Cookies and site permissions → Cookies and site data</p>
              </div>
            </div>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">What Happens When You Disable Cookies?</h3>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <ul className="space-y-2 text-yellow-800 text-sm">
                <li className="flex items-start">
                  <XMarkIcon className="w-4 h-4 mt-0.5 mr-2 flex-shrink-0" />
                  <span>You may need to log in repeatedly</span>
                </li>
                <li className="flex items-start">
                  <XMarkIcon className="w-4 h-4 mt-0.5 mr-2 flex-shrink-0" />
                  <span>Preferences and settings won't be saved</span>
                </li>
                <li className="flex items-start">
                  <XMarkIcon className="w-4 h-4 mt-0.5 mr-2 flex-shrink-0" />
                  <span>Some features may not work properly</span>
                </li>
                <li className="flex items-start">
                  <XMarkIcon className="w-4 h-4 mt-0.5 mr-2 flex-shrink-0" />
                  <span>Website performance may be reduced</span>
                </li>
              </ul>
            </div>
          </section>

          {/* GDPR & Legal Basis */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">GDPR & Legal Basis</h2>
            
            <div className="grid md:grid-cols-3 gap-4">
              <div className="p-4 border border-gray-200 rounded-lg">
                <h3 className="font-semibold text-gray-800 mb-2">Essential Cookies</h3>
                <p className="text-sm text-gray-600 mb-1"><strong>Legal Basis:</strong> Legitimate Interest</p>
                <p className="text-xs text-gray-500">Necessary for website security and basic functionality</p>
              </div>
              <div className="p-4 border border-gray-200 rounded-lg">
                <h3 className="font-semibold text-gray-800 mb-2">Analytics Cookies</h3>
                <p className="text-sm text-gray-600 mb-1"><strong>Legal Basis:</strong> Consent</p>
                <p className="text-xs text-gray-500">You can withdraw consent at any time</p>
              </div>
              <div className="p-4 border border-gray-200 rounded-lg">
                <h3 className="font-semibold text-gray-800 mb-2">Functional Cookies</h3>
                <p className="text-sm text-gray-600 mb-1"><strong>Legal Basis:</strong> Consent</p>
                <p className="text-xs text-gray-500">Enhances your user experience</p>
              </div>
            </div>
          </section>

          {/* Data Retention */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Retention</h2>
            <p className="text-gray-700 mb-4">
              Cookie data is automatically deleted according to the expiration periods listed above. We don't store cookie data longer than necessary for its intended purpose.
            </p>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="font-semibold text-green-800 mb-2">🔄 Automatic Cleanup</h3>
              <p className="text-green-700 text-sm">
                Our systems automatically purge expired cookie data and anonymize analytics information older than 26 months, 
                in compliance with GDPR requirements.
              </p>
            </div>
          </section>

          {/* Contact & Updates */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Updates & Contact</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Policy Updates</h3>
            <p className="text-gray-700 mb-4">
              We may update this Cookies Policy periodically. Material changes will be communicated via email 
              or prominent notice on our website.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Contact Us</h3>
            <div className="bg-gray-50 rounded-lg p-6">
              <p className="text-gray-700 mb-4">
                Questions about our use of cookies? We're happy to help:
              </p>
              <div className="space-y-2 text-gray-700">
                <p><strong>Email:</strong> <a href="mailto:hello@connectco.app" className="text-purple-600 hover:text-purple-700">hello@connectco.app</a></p>
                <p><strong>Subject Line:</strong> Cookies Policy Inquiry</p>
                <p><strong>Response Time:</strong> We respond to all privacy inquiries within 72 hours</p>
              </div>
            </div>
          </section>

          {/* Legal Disclaimer */}
           {/*
          <section className="border-t pt-6">
            <p className="text-sm text-gray-500 italic">
              <strong>Legal Note:</strong> This cookies policy is provided as a template and for informational purposes. It should be reviewed by qualified legal counsel to ensure compliance with applicable laws in your jurisdiction before implementation.
            </p>
          </section>
          */}
        </div>
      </div>
    </div>
  );
}

export default CookiesPolicy;